import React from 'react';
import { Copy, Check, Loader2 } from 'lucide-react';

import { useClipboard } from '../../hooks/useClipboard';

/**
 * @component CopyButton
 * @description Botón para copiar texto al clipboard con feedback visual
 */

interface CopyButtonProps {
  /** Texto a copiar */
  text: string;
  /** Texto a mostrar en el tooltip */
  tooltip?: string;
  /** Variante del botón */
  variant?: 'default' | 'ghost' | 'outline' | 'minimal';
  /** Tamaño del botón */
  size?: 'sm' | 'md' | 'lg';
  /** Mostrar el texto junto al icono */
  showText?: boolean;
  /** Texto personalizado para mostrar */
  label?: string;
  /** Clases CSS adicionales */
  className?: string;
  /** Callback cuando se copia exitosamente */
  onCopy?: (text: string) => void;
  /** Callback cuando falla la copia */
  onError?: (error: Error) => void;
  /** Deshabilitado */
  disabled?: boolean;
}

const CopyButton: React.FC<CopyButtonProps> = ({
  text,
  tooltip,
  variant = 'default',
  size = 'md',
  showText = false,
  label = 'Copiar',
  className = '',
  onCopy,
  onError,
  disabled = false,
}) => {
  const { copy, copied, loading, error } = useClipboard({
    onSuccess: onCopy,
    onError,
    feedbackDuration: 2000,
  });

  const handleCopy = async () => {
    if (disabled || loading) return;
    await copy(text);
  };

  // Clases base según variante
  const getVariantClasses = () => {
    switch (variant) {
      case 'ghost':
        return 'text-gray-500 hover:text-gray-700 hover:bg-gray-100';
      case 'outline':
        return 'border border-gray-300 text-gray-700 hover:bg-gray-50';
      case 'minimal':
        return 'text-gray-400 hover:text-gray-600';
      default:
        return 'bg-primary-50 text-primary-600 hover:bg-primary-100';
    }
  };

  // Clases de tamaño
  const getSizeClasses = () => {
    switch (size) {
      case 'sm':
        return 'p-1 text-xs';
      case 'lg':
        return 'p-3 text-base';
      default:
        return 'p-2 text-sm';
    }
  };

  // Tamaño del icono
  const getIconSize = () => {
    switch (size) {
      case 'sm':
        return 'h-3 w-3';
      case 'lg':
        return 'h-5 w-5';
      default:
        return 'h-4 w-4';
    }
  };

  const buttonClasses = [
    'inline-flex items-center gap-2 rounded-md font-medium transition-colors duration-200',
    'focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2',
    'disabled:opacity-50 disabled:cursor-not-allowed',
    getVariantClasses(),
    getSizeClasses(),
    className
  ].filter(Boolean).join(' ');

  const iconClasses = getIconSize();

  // Determinar qué icono mostrar
  const renderIcon = () => {
    if (loading) {
      return <Loader2 className={`${iconClasses} animate-spin`} />;
    }
    if (copied) {
      return <Check className={`${iconClasses} text-green-600`} />;
    }
    return <Copy className={iconClasses} />;
  };

  // Determinar el texto a mostrar
  const getButtonText = () => {
    if (loading) return 'Copiando...';
    if (copied) return 'Copiado';
    return label;
  };

  return (
    <button
      type="button"
      onClick={handleCopy}
      disabled={disabled || loading}
      className={buttonClasses}
      title={tooltip || (copied ? 'Copiado' : `Copiar ${text}`)}
      aria-label={copied ? 'Copiado al clipboard' : `Copiar ${text} al clipboard`}
    >
      {renderIcon()}
      {showText && (
        <span className={copied ? 'text-green-600' : ''}>
          {getButtonText()}
        </span>
      )}
    </button>
  );
};

export default CopyButton;

/**
 * @component CopyText
 * @description Componente que muestra texto con botón de copiar integrado
 */

interface CopyTextProps {
  /** Texto a mostrar y copiar */
  text: string;
  /** Texto truncado para mostrar (opcional) */
  displayText?: string;
  /** Mostrar texto completo o truncado */
  truncate?: boolean;
  /** Longitud máxima antes de truncar */
  maxLength?: number;
  /** Clases CSS para el contenedor */
  className?: string;
  /** Clases CSS para el texto */
  textClassName?: string;
  /** Variante del botón de copiar */
  buttonVariant?: CopyButtonProps['variant'];
  /** Callback cuando se copia */
  onCopy?: (text: string) => void;
}

export const CopyText: React.FC<CopyTextProps> = ({
  text,
  displayText,
  truncate = false,
  maxLength = 20,
  className = '',
  textClassName = '',
  buttonVariant = 'ghost',
  onCopy,
}) => {
  // Determinar qué texto mostrar
  const getDisplayText = () => {
    if (displayText) return displayText;
    if (truncate && text.length > maxLength) {
      return `${text.substring(0, maxLength)}...`;
    }
    return text;
  };

  return (
    <div className={`inline-flex items-center gap-2 ${className}`}>
      <span className={`font-mono ${textClassName}`} title={text}>
        {getDisplayText()}
      </span>
      <CopyButton
        text={text}
        variant={buttonVariant}
        size="sm"
        onCopy={onCopy}
        tooltip={`Copiar ${text}`}
      />
    </div>
  );
};

/**
 * @component CopyableCode
 * @description Componente para mostrar código con funcionalidad de copia
 */

interface CopyableCodeProps {
  /** Código a mostrar */
  code: string;
  /** Lenguaje del código (para highlighting futuro) */
  language?: string;
  /** Clases CSS adicionales */
  className?: string;
  /** Mostrar números de línea */
  showLineNumbers?: boolean;
}

export const CopyableCode: React.FC<CopyableCodeProps> = ({
  code,
  language,
  className = '',
  showLineNumbers = false,
}) => {
  const lines = code.split('\n');

  return (
    <div className={`relative bg-gray-900 text-gray-100 rounded-lg overflow-hidden ${className}`}>
      {/* Header con botón de copiar */}
      <div className="flex items-center justify-between px-4 py-2 bg-gray-800 border-b border-gray-700">
        <span className="text-xs text-gray-400 uppercase tracking-wide">
          {language || 'código'}
        </span>
        <CopyButton
          text={code}
          variant="ghost"
          size="sm"
          showText={true}
          label="Copiar"
          className="text-gray-300 hover:text-white hover:bg-gray-700"
        />
      </div>

      {/* Contenido del código */}
      <div className="p-4 overflow-x-auto">
        <pre className="text-sm">
          {showLineNumbers ? (
            <div className="flex">
              <div className="flex flex-col text-gray-500 text-right pr-4 select-none">
                {lines.map((_, index) => (
                  <span key={index} className="leading-6">
                    {index + 1}
                  </span>
                ))}
              </div>
              <code className="flex-1">
                {lines.map((line, index) => (
                  <div key={index} className="leading-6">
                    {line}
                  </div>
                ))}
              </code>
            </div>
          ) : (
            <code>{code}</code>
          )}
        </pre>
      </div>
    </div>
  );
};
